// src/services/mcpAuthApi.js
import { apiClient } from './apiClient';
import { STORAGE_KEYS } from '../config/cognito';

/**
 * MCP session management API (broker pattern).
 *
 * The frontend is the only place in the codebase that copies the user's
 * Cognito refresh_token from localStorage onto an outbound request: we
 * forward it via X-Cognito-Refresh-Token to `POST /sessions`. The backend
 * encrypts and stores it so the MCP server can mint short-lived access
 * tokens on the user's behalf for the lifetime of the session.
 */
function getRefreshToken() {
  const t = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  if (!t) {
    throw new Error('Cognito refresh token not in localStorage — please log in again.');
  }
  return t;
}

export const mcpAuthApi = {
  /**
   * Mint an IDE session directly (frontend-initiated; no pair-code step).
   * @param {Object} params
   * @param {string|null} [params.deviceLabel]
   * @returns {Promise<{session_token: string, session_id: string, expires_at: string}>}
   */
  createSession: async ({ deviceLabel } = {}) => {
    return apiClient.request('/auth/mcp/sessions', {
      method: 'POST',
      headers: {
        'X-Cognito-Refresh-Token': getRefreshToken(),
      },
      body: JSON.stringify({ device_label: deviceLabel ?? null }),
    });
  },

  /**
   * List the user's active MCP sessions.
   * @returns {Promise<{sessions: Array<{id, user_id, device_label, ip, user_agent, paired_at, last_used, expires_at, pair_code_id}>}>}
   */
  listSessions: async () => {
    return apiClient.request('/auth/mcp/sessions', {
      method: 'GET',
    });
  },

  /**
   * Revoke a session.
   * @param {string} sessionId
   * @returns {Promise<{revoked: boolean}>}
   */
  revokeSession: async (sessionId) => {
    return apiClient.request(`/auth/mcp/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
    });
  },
};
